import defaultImage from '@/assets/media/images/default-user.png';
import ImageModal from '@/components/common/ImageModal/ImageModal';
import Paginator from '@/components/Paginator/Paginator';
import { UserService } from '@/services/UserService';
import { DefaultRowsPerPage, RELATION } from '@/utils/constant/constant';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import '../../../assets/styles/react-datepicker.css';
import DeleteConfirmModal from '../../common/DeleteConfirmModal/DeleteConfirmModal';
import SubMemberEditModal from './SubMemberEditModal';
import './SubMembers.scss';

type Member = {
  id: number;
  club_membership_id: string;
  club_membership_id_qr: string;
  first_name: string;
  last_name: string;
  relation: string;
  date_of_birth: string;
  email: string;
  photo: string;
  photo_key: string;
};

const initialMember: Member = {
  id: 0,
  club_membership_id: '',
  club_membership_id_qr: '',
  first_name: '',
  last_name: '',
  relation: '',
  date_of_birth: '',
  photo: '',
  photo_key: '',
  email: '',
};

function SubMembers() {
  const [subMembers, setSubMembers] = useState<Member[]>([]);
  const [totalSubMembers, setTotalSubMembers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalMember, setEditModalMember] = useState<Member>(initialMember);
  const [editModalIsEdit, setEditModalIsEdit] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{
    idx: number;
    member: Member;
  } | null>(null);
  const [showImageModal, setShowImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    imageName: '',
  });

  const openEditModal = (idx: number) => {
    setEditModalMember({
      ...subMembers[idx],
    });
    setEditModalIsEdit(true);
    setShowEditModal(true);
  };

  const addMember = () => {
    setEditModalMember(initialMember);
    setEditModalIsEdit(false);
    setShowEditModal(true);
  };

  const openDeleteModal = (idx: number) => {
    setMemberToDelete({ idx, member: subMembers[idx] });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const confirmDeleteSubMember = () => {
    if (!memberToDelete) return;

    const { idx } = memberToDelete;
    const id = subMembers[idx].id;
    if (!id) return;

    try {
      UserService.deleteSubMember(id).then((res) => {
        if (res?.data) {
          toast.success('Sub member deleted successfully.');
          if (page === 1) {
            fetchSubMembers();
          } else {
            setPage(1);
          }
          closeDeleteModal();
        }
      });
    } catch (error) {
      toast.error('Some error occurred while deleting sub member.');
      closeDeleteModal();
    }
  };

  const fetchSubMembers = async (page: number = 1) => {
    const response = await UserService.getSubMembers(DefaultRowsPerPage, page);
    if (response?.data?.data) {
      setSubMembers(response?.data?.data.rows);
      setTotalSubMembers(response?.data?.data.count);
    }
  };

  useEffect(() => {
    fetchSubMembers(page);
  }, [page]);

  return (
    <div className="profile-main">
      <div className="profile-content">
        <div className="head-profile">
          <h1 className="page-title">Family Members / Guests</h1>
          <button
            type="button"
            className="btn btn--sm btn--pri"
            onClick={addMember}
          >
            Add Family Member / Guest
          </button>
        </div>

        <div className="sub-members-table">
          <div className="sub-members-heading-row">
            <div className="heading-cell"></div>
            <div className="heading-cell-field">
              <div className="heading-cell">Member</div>
              <div className="heading-cell">Membership ID</div>
              <div className="heading-cell">Relation</div>
              <div className="heading-cell">Date of Birth</div>
            </div>
            <div className="heading-cell">Action</div>
          </div>
          {subMembers.length === 0 ? (
            <div className="no-record-found">No record found</div>
          ) : (
            subMembers.map((subMember, idx) => (
              <div
                className="member-list-item"
                key={`${idx}_${subMember.photo_key}`}
              >
                <div className="user-name field-item">
                  <figure key={subMember.photo_key}>
                    <img src={subMember.photo || defaultImage} alt="" />
                  </figure>
                </div>
                <div className="member-info-fields">
                  <div className="user-name field-item">
                    <label className="cell-field-title">Name</label>
                    <span>
                      {subMember.first_name} {subMember.last_name}
                      {subMember.email && (
                        <small className="email">{subMember.email}</small>
                      )}
                    </span>
                  </div>
                  <div className="relation field-item">
                    <label className="cell-field-title">Membership ID</label>
                    <span>{subMember.club_membership_id || 'N/A'}</span>
                  </div>
                  <div className="relation field-item">
                    <label className="cell-field-title">Relation</label>
                    <span>
                      {RELATION[subMember.relation as keyof typeof RELATION]}
                    </span>
                  </div>
                  <div className="date-of-birth field-item">
                    <label className="cell-field-title">Date of Birth</label>
                    <span>
                      {moment(subMember.date_of_birth, 'YYYY-MM-DD').format(
                        'MM/DD/YYYY'
                      )}
                    </span>
                  </div>
                </div>
                <div className="action-item">
                  {!!subMember.club_membership_id_qr && (
                    <button
                      title="Download Membership ID QR"
                      type="button"
                      className="btn-icon qr-code-action"
                      onClick={() =>
                        setShowImageModal({
                          isOpen: true,
                          imageUrl: subMember.club_membership_id_qr,
                          imageName: `${subMember.first_name}_${subMember.last_name}_restaurant_membership_id_.png`,
                        })
                      }
                    >
                      <img src={subMember.club_membership_id_qr} alt="" />
                    </button>
                  )}
                  <button
                    title="Edit"
                    type="button"
                    className="btn-icon btn-edit"
                    onClick={() => openEditModal(idx)}
                  >
                    <i className="cs-icon icon-edit"></i>
                  </button>
                  <button
                    title="Delete"
                    type="button"
                    className="btn-icon"
                    onClick={() => openDeleteModal(idx)}
                  >
                    <i className="cs-icon icon-trash"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        {totalSubMembers > DefaultRowsPerPage && (
          <Paginator
            count={DefaultRowsPerPage}
            totalCount={totalSubMembers}
            currentPage={page}
            onPageChange={setPage}
          />
        )}

        <SubMemberEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          refresh={() => {
            if (page === 1) {
              fetchSubMembers();
            } else {
              setPage(1);
            }
          }}
          member={editModalMember}
          isEdit={editModalIsEdit}
        />

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          message={`Are you sure you would like to delete ${
            memberToDelete?.member
              ? `${memberToDelete.member.first_name} ${memberToDelete.member.last_name}`.trim() ||
                'this family member'
              : 'this family member'
          }?`}
          onConfirm={confirmDeleteSubMember}
          onCancel={closeDeleteModal}
        />
        <ImageModal
          isOpen={showImageModal.isOpen}
          onClose={() =>
            setShowImageModal({ isOpen: false, imageUrl: '', imageName: '' })
          }
          imageUrl={showImageModal.imageUrl}
          imageName={showImageModal.imageName}
          isDownloadable={true}
        />
      </div>
    </div>
  );
}

export default SubMembers;
